import type {
  ActionType,
  Facing,
  SequenceRepeatToken,
  SequenceToken,
} from "../types/action";
import type { ChapterConnection, ChapterData, ChapterKind, ChapterNode, ChapterNodeType, ChapterUnlockHintDisplay, ChapterUnlockRequirement } from "../types/chapter";
import type { GridPoint } from "../types/grid";
import type { BreadcrumbBehaviorData, LevelBreadcrumbData, LevelCameraData, LevelCameraMode, LevelCollectibleData, LevelData, LevelGridData, LevelInitialVisibility, LevelMetadata, LevelPortalData, LevelRuntimeConstraints } from "../types/level";
import type { GameSave, SaveCollectibles, SaveFog, SaveProgression, SaveSymbols } from "../types/save";
import type { SymbolDefinition, SymbolDictionaryData } from "../types/symbol";

const VALID_ACTIONS: readonly ActionType[] = [
  "left",
  "right",
  "up",
  "down",
  "jump",
  "undo",
];

const VALID_FACING: readonly Facing[] = ["left", "right"];

const VALID_CHAPTER_KINDS: readonly ChapterKind[] = ["chapter", "ending"];

const VALID_NODE_TYPES: readonly ChapterNodeType[] = ["lock", "level", "ending"];

const VALID_VISIBILITY_MODES: ReadonlyArray<"radius" | "rects"> = [
  "radius",
  "rects",
];

const VALID_CAMERA_MODES: readonly LevelCameraMode[] = ["static", "follow"];

const VALID_REPEAT_TOKENS: readonly SequenceRepeatToken[] = ["3", "5"];

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isNumber(value: unknown): value is number {
  return typeof value === "number" && !Number.isNaN(value);
}

function isInteger(value: unknown): value is number {
  return isNumber(value) && Number.isInteger(value);
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function assertField(
  obj: Record<string, unknown>,
  key: string,
  predicate: (v: unknown) => boolean,
  message: string,
): void {
  if (!(key in obj) || obj[key] === undefined) {
    throw new Error(`Missing required field: ${key}`);
  }
  const value = obj[key];
  if (!predicate(value)) {
    throw new Error(`Invalid field ${key}: ${message}`);
  }
}

function validateActionType(value: unknown): ActionType {
  if (!isString(value) || !(VALID_ACTIONS as readonly string[]).includes(value)) {
    throw new Error(`Invalid action type: ${String(value)}`);
  }
  return value as ActionType;
}

function validateGridPoint(value: unknown): GridPoint {
  if (!isObject(value)) {
    throw new Error("GridPoint must be an object");
  }
  assertField(value, "x", isInteger, "must be an integer");
  assertField(value, "y", isInteger, "must be an integer");
  return { x: value["x"] as number, y: value["y"] as number };
}

function validateSymbolDefinition(value: unknown): SymbolDefinition {
  if (!isObject(value)) {
    throw new Error("Symbol definition must be an object");
  }
  assertField(value, "token", isString, "must be a string");
  assertField(value, "action", (v) => VALID_ACTIONS.includes(validateActionType(v)), "must be a valid action type");
  return {
    token: value["token"] as string,
    action: validateActionType(value["action"]),
  };
}

export function validateSymbolDictionaryData(value: unknown): SymbolDictionaryData {
  if (!isObject(value)) {
    throw new Error("Symbol dictionary must be an object");
  }
  assertField(value, "version", isInteger, "must be an integer");
  assertField(value, "symbols", isArray, "must be an array");
  const symbols = (value["symbols"] as unknown[]).map(validateSymbolDefinition);
  return {
    version: value["version"] as number,
    symbols,
  };
}

function validateChapterUnlockRequirement(value: unknown): ChapterUnlockRequirement {
  if (!isObject(value)) {
    throw new Error("Unlock requirement must be an object");
  }

  assertField(value, "password", isArray, "must be an array");
  const password = (value["password"] as unknown[]).map(validateActionType);

  let requiredPreviousChapterCompletedLevels = 0;
  if (value["requiredPreviousChapterCompletedLevels"] !== undefined) {
    if (!isInteger(value["requiredPreviousChapterCompletedLevels"]) || (value["requiredPreviousChapterCompletedLevels"] as number) < 0) {
      throw new Error("Invalid field requiredPreviousChapterCompletedLevels: must be a non-negative integer");
    }
    requiredPreviousChapterCompletedLevels = value["requiredPreviousChapterCompletedLevels"] as number;
  } else if (value["requiredCollectibleCount"] !== undefined) {
    if (!isInteger(value["requiredCollectibleCount"]) || (value["requiredCollectibleCount"] as number) < 0) {
      throw new Error("Invalid field requiredCollectibleCount: must be a non-negative integer");
    }
    requiredPreviousChapterCompletedLevels = value["requiredCollectibleCount"] as number;
  }

  let hintDisplay: ChapterUnlockHintDisplay = { symbolSequence: [], hint: "" };
  if (value["hintDisplay"] !== undefined) {
    if (!isObject(value["hintDisplay"])) {
      throw new Error("Invalid field hintDisplay: must be an object");
    }
    const hintDisplayObj = value["hintDisplay"] as Record<string, unknown>;

    let symbolSequence: readonly SequenceToken[] = [];
    if (hintDisplayObj["symbolSequence"] !== undefined) {
      if (!isArray(hintDisplayObj["symbolSequence"])) {
        throw new Error("Invalid field hintDisplay.symbolSequence: must be an array");
      }
      symbolSequence = (hintDisplayObj["symbolSequence"] as unknown[]).map(validateSequenceToken);
    }

    let hint = "";
    if (hintDisplayObj["hint"] !== undefined) {
      if (!isString(hintDisplayObj["hint"])) {
        throw new Error("Invalid field hintDisplay.hint: must be a string");
      }
      hint = hintDisplayObj["hint"] as string;
    }

    hintDisplay = { symbolSequence, hint };
  }

  const requiredCollectibleCount = requiredPreviousChapterCompletedLevels;

  return {
    requiredCollectibleCount,
    password,
    requiredPreviousChapterCompletedLevels,
    hintDisplay,
  };
}

function validateChapterNode(value: unknown): ChapterNode {
  if (!isObject(value)) {
    throw new Error("Chapter node must be an object");
  }
  assertField(value, "id", isString, "must be a string");
  assertField(value, "type", (v) => isString(v) && VALID_NODE_TYPES.includes(v as ChapterNodeType), "must be lock, level, or ending");
  assertField(value, "x", isInteger, "must be an integer");
  assertField(value, "y", isInteger, "must be an integer");
  return {
    id: value["id"] as string,
    type: value["type"] as ChapterNodeType,
    x: value["x"] as number,
    y: value["y"] as number,
  };
}

function validateChapterConnection(value: unknown): ChapterConnection {
  if (!isObject(value)) {
    throw new Error("Chapter connection must be an object");
  }
  assertField(value, "from", isString, "must be a string");
  assertField(value, "to", isString, "must be a string");
  return {
    from: value["from"] as string,
    to: value["to"] as string,
  };
}

export function validateChapterData(value: unknown): ChapterData {
  if (!isObject(value)) {
    throw new Error("Chapter data must be an object");
  }
  assertField(value, "id", isString, "must be a string");
  assertField(value, "kind", (v) => isString(v) && VALID_CHAPTER_KINDS.includes(v as ChapterKind), "must be chapter or ending");
  assertField(value, "order", (v) => isInteger(v) && (v as number) >= 0, "must be a non-negative integer");
  assertField(value, "name", isString, "must be a string");
  assertField(value, "teaches", isArray, "must be an array");
  const teaches = (value["teaches"] as unknown[]).map(validateSequenceToken);
  assertField(value, "collectibleType", isString, "must be a string");
  assertField(value, "levels", isArray, "must be an array");
  const levels = (value["levels"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("Level ID must be a string");
    }
    return v;
  });
  assertField(value, "nodes", isArray, "must be an array");
  const nodes = (value["nodes"] as unknown[]).map(validateChapterNode);
  assertField(value, "connections", isArray, "must be an array");
  const connections = (value["connections"] as unknown[]).map(validateChapterConnection);

  const base = {
    id: value["id"] as string,
    kind: value["kind"] as ChapterKind,
    order: value["order"] as number,
    name: value["name"] as string,
    teaches,
    collectibleType: value["collectibleType"] as string,
    levels,
    nodes,
    connections,
  };

  if (value["unlock"] !== undefined) {
    const unlock = validateChapterUnlockRequirement(value["unlock"]);
    return { ...base, unlock };
  }

  return base as ChapterData;
}

function validateLevelGridData(value: unknown): LevelGridData {
  if (!isObject(value)) {
    throw new Error("Grid data must be an object");
  }
  assertField(value, "width", (v) => isInteger(v) && (v as number) > 0, "must be a positive integer");
  assertField(value, "height", (v) => isInteger(v) && (v as number) > 0, "must be a positive integer");
  assertField(value, "tiles", isArray, "must be an array");
  assertField(value, "planks", isArray, "must be an array");

  const width = value["width"] as number;
  const height = value["height"] as number;
  const tiles = value["tiles"] as unknown[];

  if (tiles.length !== width * height) {
    throw new Error(`Tiles array length (${tiles.length}) must equal width * height (${width * height})`);
  }

  for (let i = 0; i < tiles.length; i++) {
    const tile = tiles[i];
    if (!isInteger(tile) || tile < 0 || tile > 2) {
      throw new Error(`Invalid tile value at index ${i}: ${String(tile)}. Must be 0, 1, or 2`);
    }
  }

  const planks = (value["planks"] as unknown[]).map(validateGridPoint);

  return { width, height, tiles: tiles as number[], planks };
}

function validateLevelCameraData(value: unknown): LevelCameraData {
  if (!isObject(value)) {
    throw new Error("Camera data must be an object");
  }
  assertField(value, "initialCenter", isObject, "must be an object");
  const initialCenter = validateGridPoint(value["initialCenter"]);

  const rawMode = value["mode"];
  if (rawMode !== undefined) {
    if (!isString(rawMode) || !VALID_CAMERA_MODES.includes(rawMode as LevelCameraMode)) {
      throw new Error(`Invalid field mode: must be "static" or "follow"`);
    }
    return { initialCenter, mode: rawMode as LevelCameraMode };
  }

  return { initialCenter };
}

function validateLevelCollectibleData(value: unknown): LevelCollectibleData {
  if (!isObject(value)) {
    throw new Error("Collectible data must be an object");
  }
  assertField(value, "id", isString, "must be a string");
  assertField(value, "type", isString, "must be a string");
  assertField(value, "x", isInteger, "must be an integer");
  assertField(value, "y", isInteger, "must be an integer");
  const group = isString(value["group"]) ? (value["group"] as string) : "";
  return {
    id: value["id"] as string,
    type: value["type"] as string,
    group,
    x: value["x"] as number,
    y: value["y"] as number,
  };
}

function validateBreadcrumbBehaviorData(value: unknown): BreadcrumbBehaviorData {
  if (!isObject(value)) {
    throw new Error("Breadcrumb behavior data must be an object");
  }
  assertField(value, "type", (v) => isString(v) && (v === "dialog" || v === "tip"), 'must be "dialog" or "tip"');
  assertField(value, "text", isString, "must be a string");
  assertField(value, "during", isNumber, "must be a number");
  return {
    type: value["type"] as "dialog" | "tip",
    text: value["text"] as string,
    during: value["during"] as number,
  };
}

function validateLevelBreadcrumbData(value: unknown): LevelBreadcrumbData {
  if (!isObject(value)) {
    throw new Error("Breadcrumb data must be an object");
  }
  assertField(value, "id", isString, "must be a string");
  assertField(value, "x", isInteger, "must be an integer");
  assertField(value, "y", isInteger, "must be an integer");
  const showIcon = isBoolean(value["showIcon"]) ? (value["showIcon"] as boolean) : false;
  const uniqueTrigger = isBoolean(value["uniqueTrigger"]) ? (value["uniqueTrigger"] as boolean) : false;
  const triggerOnLevelStart = isBoolean(value["triggerOnLevelStart"])
    ? (value["triggerOnLevelStart"] as boolean)
    : false;
  let behavior: BreadcrumbBehaviorData | undefined;
  if (value["behavior"] !== undefined) {
    behavior = validateBreadcrumbBehaviorData(value["behavior"]);
  }
  return {
    id: value["id"] as string,
    x: value["x"] as number,
    y: value["y"] as number,
    showIcon,
    uniqueTrigger,
    triggerOnLevelStart,
    ...(behavior !== undefined ? { behavior } : {}),
  };
}

function validateLevelPortalData(value: unknown): LevelPortalData {
  if (!isObject(value)) {
    throw new Error("Portal data must be an object");
  }
  assertField(value, "id", isString, "must be a string");
  assertField(value, "group", isString, "must be a string");
  assertField(value, "x", isInteger, "must be an integer");
  assertField(value, "y", isInteger, "must be an integer");
  return {
    id: value["id"] as string,
    group: value["group"] as string,
    x: value["x"] as number,
    y: value["y"] as number,
  };
}

function validateLevelInitialVisibility(value: unknown): LevelInitialVisibility {
  if (!isObject(value)) {
    throw new Error("Initial visibility must be an object");
  }
  assertField(value, "mode", (v) => isString(v) && VALID_VISIBILITY_MODES.includes(v as "radius" | "rects"), "must be radius or rects");
  assertField(value, "radius", (v) => isInteger(v) && (v as number) >= 0, "must be a non-negative integer");
  assertField(value, "rects", isArray, "must be an array");

  const rects = (value["rects"] as unknown[]).map((rect) => {
    if (!isObject(rect)) {
      throw new Error("Visibility rect must be an object");
    }
    assertField(rect, "x", isInteger, "must be an integer");
    assertField(rect, "y", isInteger, "must be an integer");
    assertField(rect, "width", (v) => isInteger(v) && (v as number) >= 0, "must be a non-negative integer");
    assertField(rect, "height", (v) => isInteger(v) && (v as number) >= 0, "must be a non-negative integer");
    return {
      x: rect["x"] as number,
      y: rect["y"] as number,
      width: rect["width"] as number,
      height: rect["height"] as number,
    };
  });

  return {
    mode: value["mode"] as "radius" | "rects",
    radius: value["radius"] as number,
    rects,
  };
}

function validateLevelMetadata(value: unknown): LevelMetadata {
  if (!isObject(value)) {
    throw new Error("Level metadata must be an object");
  }
  assertField(value, "requiredKnowledge", isArray, "must be an array");
  const requiredKnowledge = (value["requiredKnowledge"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("requiredKnowledge entry must be a string");
    }
    return v;
  });
  assertField(value, "introducedKnowledge", isArray, "must be an array");
  const introducedKnowledge = (value["introducedKnowledge"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("introducedKnowledge entry must be a string");
    }
    return v;
  });
  assertField(value, "hint", isString, "must be a string");
  return { requiredKnowledge, introducedKnowledge, hint: value["hint"] as string };
}

function validateLevelRuntimeConstraints(value: unknown): LevelRuntimeConstraints {
  if (!isObject(value)) {
    throw new Error("Runtime constraints must be an object");
  }
  assertField(value, "promptText", isString, "must be a string");
  assertField(value, "hideNonPlayerEntities", isBoolean, "must be a boolean");
  assertField(value, "disableNavigation", isBoolean, "must be a boolean");
  assertField(value, "skipCompletionPersistence", isBoolean, "must be a boolean");
  return {
    promptText: value["promptText"] as string,
    hideNonPlayerEntities: value["hideNonPlayerEntities"] as boolean,
    disableNavigation: value["disableNavigation"] as boolean,
    skipCompletionPersistence: value["skipCompletionPersistence"] as boolean,
  };
}

function validateSequenceToken(value: unknown): SequenceToken {
  if (!isString(value)) {
    throw new Error(`Sequence token must be a string, got ${String(value)}`);
  }
  if (VALID_REPEAT_TOKENS.includes(value as SequenceRepeatToken)) {
    return value as SequenceRepeatToken;
  }
  return value;
}

export function validateLevelData(value: unknown): LevelData {
  if (!isObject(value)) {
    throw new Error("Level data must be an object");
  }
  assertField(value, "id", isString, "must be a string");
  assertField(value, "chapter", (v) => isInteger(v) && (v as number) >= 0, "must be a non-negative integer");
  assertField(value, "order", (v) => isInteger(v) && (v as number) >= 1, "must be a positive integer");
  assertField(value, "name", isString, "must be a string");
  assertField(value, "grid", isObject, "must be an object");
  const grid = validateLevelGridData(value["grid"]);
  assertField(value, "spawn", isObject, "must be an object");
  const spawnObj = value["spawn"] as Record<string, unknown>;
  assertField(spawnObj, "x", isInteger, "must be an integer");
  assertField(spawnObj, "y", isInteger, "must be an integer");
  assertField(spawnObj, "facing", (v) => isString(v) && VALID_FACING.includes(v as Facing), "must be left or right");
  const spawn = {
    x: spawnObj["x"] as number,
    y: spawnObj["y"] as number,
    facing: spawnObj["facing"] as Facing,
  };
  assertField(value, "camera", isObject, "must be an object");
  const camera = validateLevelCameraData(value["camera"]);
  assertField(value, "exit", isObject, "must be an object");
  const exit = validateGridPoint(value["exit"]);
  assertField(value, "collectibles", isArray, "must be an array");
  const collectibles = (value["collectibles"] as unknown[]).map(validateLevelCollectibleData);
  assertField(value, "breadcrumbs", isArray, "must be an array");
  const breadcrumbs = (value["breadcrumbs"] as unknown[]).map(validateLevelBreadcrumbData);
  assertField(value, "portals", isArray, "must be an array");
  const portals = (value["portals"] as unknown[]).map(validateLevelPortalData);
  assertField(value, "symbolSequence", isArray, "must be an array");
  const symbolSequence = (value["symbolSequence"] as unknown[]).map(validateSequenceToken);
  assertField(value, "initialVisibility", isObject, "must be an object");
  const initialVisibility = validateLevelInitialVisibility(value["initialVisibility"]);
  assertField(value, "metadata", isObject, "must be an object");
  const metadata = validateLevelMetadata(value["metadata"]);

  const base = {
    id: value["id"] as string,
    chapter: value["chapter"] as number,
    order: value["order"] as number,
    name: value["name"] as string,
    grid,
    spawn,
    camera,
    exit,
    collectibles,
    breadcrumbs,
    portals,
    symbolSequence,
    initialVisibility,
    metadata,
  };

  if (value["runtimeConstraints"] !== undefined) {
    return { ...base, runtimeConstraints: validateLevelRuntimeConstraints(value["runtimeConstraints"]) };
  }

  return base as LevelData;
}

function validateSaveProgression(value: unknown): SaveProgression {
  if (!isObject(value)) {
    throw new Error("Save progression must be an object");
  }
  assertField(value, "unlockedLevels", isArray, "must be an array");
  const unlockedLevels = (value["unlockedLevels"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("unlockedLevels entry must be a string");
    }
    return v;
  });
  assertField(value, "clearedLevels", isArray, "must be an array");
  const clearedLevels = (value["clearedLevels"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("clearedLevels entry must be a string");
    }
    return v;
  });
  assertField(value, "perfectedLevels", isArray, "must be an array");
  const perfectedLevels = (value["perfectedLevels"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("perfectedLevels entry must be a string");
    }
    return v;
  });
  assertField(value, "unlockedChapters", isArray, "must be an array");
  const unlockedChapters = (value["unlockedChapters"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("unlockedChapters entry must be a string");
    }
    return v;
  });
  assertField(value, "passedLocks", isArray, "must be an array");
  const passedLocks = (value["passedLocks"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("passedLocks entry must be a string");
    }
    return v;
  });
  assertField(value, "endUnlocked", isBoolean, "must be a boolean");
  assertField(value, "noteUnlocked", isBoolean, "must be a boolean");

  return {
    unlockedLevels,
    clearedLevels,
    perfectedLevels,
    unlockedChapters,
    passedLocks,
    endUnlocked: value["endUnlocked"] as boolean,
    noteUnlocked: value["noteUnlocked"] as boolean,
  };
}

function validateSaveCollectibles(value: unknown): SaveCollectibles {
  if (!isObject(value)) {
    throw new Error("Save collectibles must be an object");
  }
  assertField(value, "byLevel", isObject, "must be an object");
  const byLevelObj = value["byLevel"] as Record<string, unknown>;
  const byLevel: Record<string, readonly string[]> = {};
  for (const key of Object.keys(byLevelObj)) {
    const arr = byLevelObj[key];
    if (!isArray(arr)) {
      throw new Error(`byLevel.${key} must be an array`);
    }
    byLevel[key] = arr.map((v) => {
      if (!isString(v)) {
        throw new Error(`byLevel.${key} entry must be a string`);
      }
      return v;
    });
  }

  const completedGroupsByLevel: Record<string, readonly string[]> = {};
  if (value["completedGroupsByLevel"] !== undefined) {
    if (!isObject(value["completedGroupsByLevel"])) {
      throw new Error("Invalid field completedGroupsByLevel: must be an object");
    }
    const completedGroupsObj = value["completedGroupsByLevel"] as Record<string, unknown>;
    for (const key of Object.keys(completedGroupsObj)) {
      const arr = completedGroupsObj[key];
      if (!isArray(arr)) {
        throw new Error(`completedGroupsByLevel.${key} must be an array`);
      }
      completedGroupsByLevel[key] = arr.map((v) => {
        if (!isString(v)) {
          throw new Error(`completedGroupsByLevel.${key} entry must be a string`);
        }
        return v;
      });
    }
  }

  assertField(value, "firstDiscoveredTypes", isArray, "must be an array");
  const firstDiscoveredTypes = (value["firstDiscoveredTypes"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("firstDiscoveredTypes entry must be a string");
    }
    return v;
  });

  return { byLevel, completedGroupsByLevel, firstDiscoveredTypes };
}

function validateSaveSymbols(value: unknown): SaveSymbols {
  if (!isObject(value)) {
    throw new Error("Save symbols must be an object");
  }
  assertField(value, "discovered", isArray, "must be an array");
  const discovered = (value["discovered"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("discovered entry must be a string");
    }
    return v;
  });
  assertField(value, "notes", isObject, "must be an object");
  const notesObj = value["notes"] as Record<string, unknown>;
  const notes: Record<string, string | null> = {};
  for (const key of Object.keys(notesObj)) {
    const noteValue = notesObj[key];
    if (noteValue === null) {
      notes[key] = null;
    } else if (isString(noteValue)) {
      notes[key] = noteValue;
    } else {
      throw new Error(`notes.${key} must be a string or null`);
    }
  }

  return { discovered, notes };
}

function validateSaveFog(value: unknown): SaveFog {
  if (!isObject(value)) {
    throw new Error("Save fog must be an object");
  }
  assertField(value, "permanentRevealLevels", isArray, "must be an array");
  const permanentRevealLevels = (value["permanentRevealLevels"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("permanentRevealLevels entry must be a string");
    }
    return v;
  });

  return { permanentRevealLevels };
}

export function validateGameSave(value: unknown): GameSave {
  if (!isObject(value)) {
    throw new Error("Game save must be an object");
  }
  assertField(value, "version", isInteger, "must be an integer");
  assertField(value, "progression", isObject, "must be an object");
  const progression = validateSaveProgression(value["progression"]);
  assertField(value, "collectibles", isObject, "must be an object");
  const collectibles = validateSaveCollectibles(value["collectibles"]);
  assertField(value, "symbols", isObject, "must be an object");
  const symbols = validateSaveSymbols(value["symbols"]);
  assertField(value, "fog", isObject, "must be an object");
  const fog = validateSaveFog(value["fog"]);
  assertField(value, "triggeredBreadcrumbs", isArray, "must be an array");
  const triggeredBreadcrumbs = (value["triggeredBreadcrumbs"] as unknown[]).map((v) => {
    if (!isString(v)) {
      throw new Error("triggeredBreadcrumbs entry must be a string");
    }
    return v;
  });

  return {
    version: value["version"] as number,
    progression,
    collectibles,
    symbols,
    fog,
    triggeredBreadcrumbs,
  };
}

export function createDefaultGameSave(): GameSave {
  return {
    version: 4,
    progression: {
      unlockedLevels: ["ch1-01"],
      clearedLevels: [],
      perfectedLevels: [],
      unlockedChapters: ["chapter-1"],
      passedLocks: [],
      endUnlocked: false,
      noteUnlocked: false,
    },
    collectibles: {
      byLevel: {},
      completedGroupsByLevel: {},
      firstDiscoveredTypes: [],
    },
    symbols: {
      discovered: [],
      notes: {},
    },
    fog: {
      permanentRevealLevels: [],
    },
    triggeredBreadcrumbs: [],
  };
}
